// MACHINERY V2 (Stroje) — MIDAS Data Readiness popup (M2.9).
//
// Spec: MACHINERY_V2_IMPLEMENTATION_TICKET.md §8 M2.9. Reference impl:
// design_handoff_machinery_v3/machinery-readiness.jsx (`ReadinessPopup`, 640px,
// three states) + README.md §8. Content model + the SEC-REQ-8 mailto hardening
// come from the shipped DR-3 popup (components/readiness/MidasReadinessPopup),
// re-rendered in the v2 idiom.
//
// >>> THIS IS THE ONLY FILE IN THE KIT THAT RENDERS A CTA. <<< (locked)
// Both CTAs live here: "Contact <OEM>" (gold panel, OEM only) and "Request
// MachineID integration" (outline, never when connected). No badge, banner or
// profile card may grow one — a card that wants a CTA opens this popup.
//
// ONE state, three faces: no "state" prop. It self-configures from the shared
// presentation, so it cannot disagree with the badge that opened it.
//
// PORTAL RULE (MachineryV2Layout.tsx:20-39): Dialog.Content portals to
// document.body, outside `.machinery-v2-root`, where the scoped vars do not
// resolve. Content re-carries `machinery-v2-root p-<palette> mod-stroje`, sets
// inline background:"transparent" (or the root's opaque var(--bg) slabs the
// screen behind the overlay), paints the real surface on the inner div, and
// reads `palette` live on EVERY render. This DUPLICATES ChromeRightMenus.tsx's
// `MenuContent` (canonical), unreusable here because it is hard-bound to
// DropdownMenu.Portal/.Content, which cannot live inside a Dialog.Root. Change
// all three copies together (ChromeRightMenus, CapTip, here). The Overlay
// deliberately does NOT carry the scope class — it is a fixed rgba scrim, and
// classing it would repaint var(--bg).
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";
import * as Dialog from "@radix-ui/react-dialog";

import useTranslation from "../../../../shared/hooks/useTranslation";
import { useAppSelector } from "../../../../shared/hooks/sharedHooks";
import { fanoutProvenanceLabel } from "../../utils/midasReadiness";
import {
  DocLane,
  OemSupportPanel,
  OutlineCta,
  PopupHeader,
  SectionBadge,
} from "./readinessPopupSections";
import {
  MachineryV2ReadinessPresentation,
  RD,
  V2_DOC_TYPE_KEYS,
} from "./readinessPresentation";

const SALES_MAILTO = "sales@machine-id.com";

/** Build a mailto: URL with every dynamic value encoded (SEC-REQ-8). */
const buildMailto = (email: string, subject: string, body?: string): string => {
  const q = ["subject=" + encodeURIComponent(subject)];
  if (body) q.push("body=" + encodeURIComponent(body));
  return "mailto:" + email + "?" + q.join("&");
};

/** Accept an OEM address only as EXACTLY ONE well-formed mailto target
 *  (SEC-REQ-8). Rejects, in order: `?`/`&` (extra mailto headers), whitespace
 *  incl. CR/LF (header injection), `,`/`;` (recipient separators) and `<`/`>`
 *  (address-group syntax) — then requires exactly one "@" with a non-empty
 *  local part and domain. Anything else falls back to sales@.
 *  The separator/bracket/second-@ cases were added by review fix 5: the old
 *  predicate let "a@x.com,b@y.com" through as a recipient, which its own
 *  "single, well-formed target" requirement forbade. */
const isSafeMailtoAddress = (email?: string | null): email is string => {
  if (!email || /[?&,;<>\s]/.test(email)) return false;
  const parts = email.split("@");
  return parts.length === 2 && parts[0].length > 0 && parts[1].length > 0;
};

export interface ReadinessPopupV2Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The SAME object the host gave the badges / banner / profile card. */
  presentation: MachineryV2ReadinessPresentation;
  /** Machine or catalog-model display name — the dialog title. */
  machineOrModelName: string;
  oemPartnerName?: string | null;
  /** Manufacturer display name — only for the non-OEM "not offered" line. */
  manufacturerName?: string | null;
  oemContactUrl?: string | null;
  oemContactEmail?: string | null;
}

const ReadinessPopupV2: React.FC<ReadinessPopupV2Props> = ({
  open,
  onOpenChange,
  presentation: p,
  machineOrModelName,
  oemPartnerName,
  manufacturerName,
  oemContactUrl,
  oemContactEmail,
}) => {
  const { translate } = useTranslation("myMachinery");
  const palette = useAppSelector((s) => s.machineryV2Ui.palette);
  const oemName = oemPartnerName || translate("myMachinery:theManufacturer");

  const provenanceText = (kind: string, src?: string): string =>
    kind === "direct-oem"
      ? translate("myMachinery:provenanceDirectOem", { name: oemName })
      : kind === "direct-owner"
      ? translate("myMachinery:provenanceDirectOwner")
      : kind === "fanout"
      ? translate("myMachinery:provenanceFanout", { source: fanoutProvenanceLabel(src || "") })
      : translate("myMachinery:notIngestedYet");

  const onRequestIntegration = () => {
    window.location.href = buildMailto(
      SALES_MAILTO,
      translate("myMachinery:mailRequestSubject", { name: machineOrModelName }),
      translate("myMachinery:mailRequestBody", { name: machineOrModelName })
    );
  };

  const onContactOem = () => {
    // Open a ContactUrl only when it is an http(s) URL (SEC-REQ-8).
    if (oemContactUrl && /^https?:\/\//i.test(oemContactUrl)) {
      window.open(oemContactUrl, "_blank", "noopener,noreferrer");
      return;
    }
    const email = isSafeMailtoAddress(oemContactEmail) ? oemContactEmail : SALES_MAILTO;
    window.location.href = buildMailto(
      email,
      translate("myMachinery:mailContactOemSubject", { oem: oemName, name: machineOrModelName })
    );
  };

  const connected = p.telemetry === "connected";
  const liveBadge: { label: string; tone: "ready" | "muted" } = connected
    ? { label: translate("myMachinery:liveConnected"), tone: "ready" }
    : p.telemetry === "possible"
    ? { label: translate("myMachinery:livePossible"), tone: "muted" }
    : { label: translate("myMachinery:liveNotConnected"), tone: "muted" };

  const liveBody = connected
    ? translate("myMachinery:liveConnectedBody")
    : p.oemLit
    ? translate("myMachinery:liveOemPossibleBody", { oem: oemName })
    : translate("myMachinery:liveEdgeApplianceBody");

  const section: React.CSSProperties = { padding: "0 22px", marginTop: 16, minWidth: 0 };
  const heading: React.CSSProperties = {
    display: "flex", alignItems: "center", gap: 8, fontSize: 13.5,
    fontWeight: 600, color: "var(--ink)", marginBottom: 4 };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        {/* This popup is a JSX CHILD of its opener (fleet / catalog / profile
            card). Radix portals the DOM to <body>, but React synthetic events
            still bubble along the COMPONENT tree — so a click on Close, the
            backdrop or a CTA would reach the opener card's onClick and navigate
            the user away while the mailto fires. Stopping propagation on both
            portalled nodes covers every current and future opener. Radix's own
            dismiss is unaffected (it listens for document `pointerdown`, not
            this React click). Carried from v1's MidasReadinessPopup. */}
        <Dialog.Overlay
          onClick={(e) => e.stopPropagation()}
          style={{ position: "fixed", inset: 0, background: "rgba(12, 17, 23, 0.35)", zIndex: 1300 }}
        />
        <Dialog.Content
          className={`machinery-v2-root p-${palette} mod-stroje`}
          aria-describedby={undefined}
          onClick={(e) => e.stopPropagation()}
          style={{
            position: "fixed", top: "50%", left: "50%",
            transform: "translate(-50%, -50%)", zIndex: 1301, display: "flex",
            width: 640, maxWidth: "calc(100vw - 32px)",
            maxHeight: "calc(100vh - 32px)", background: "transparent",
          }}
        >
          <div
            style={{
              flex: 1, minWidth: 0, display: "flex", flexDirection: "column",
              overflow: "hidden", background: "var(--card)", borderRadius: 12,
              border: "1px solid var(--border)",
              boxShadow: "0 24px 64px rgba(15,23,30,0.22), 0 4px 16px rgba(15,23,30,0.10)",
            }}
          >
            <div style={{ flex: 1, minHeight: 0, overflowY: "auto", paddingBottom: 18 }}>
              <PopupHeader
                eyebrow={translate("myMachinery:midasDataReadiness")}
                name={machineOrModelName}
                closeLabel={translate("myMachinery:close")}
                presentation={p}
              />

              {/* OEM showcase + CTA #1 — OEM state only */}
              {p.oemLit && (
                <div style={section}>
                  <OemSupportPanel
                    eyebrow={translate("myMachinery:fullOemSupport")}
                    title={translate("myMachinery:oemPanelTitle", { oem: oemName })}
                    body={translate("myMachinery:oemPanelBody")}
                    ctaLabel={translate("myMachinery:contactOem", { oem: oemName })}
                    onCta={onContactOem}
                  />
                </div>
              )}

              {/* Machine knowledge — doc lanes */}
              <div style={section}>
                <div style={heading}>
                  {translate("myMachinery:machineKnowledge")}
                  <SectionBadge
                    label={translate(
                      p.hasAnyDocs ? "myMachinery:knowledgeReady" : "myMachinery:knowledgeNoDocs"
                    )}
                    tone={p.hasAnyDocs ? "ready" : "muted"}
                  />
                </div>
                {p.docRows.map((row, i) => (
                  <DocLane
                    key={row.docType}
                    chipLabel={translate(V2_DOC_TYPE_KEYS[row.docType])}
                    lit={row.lit}
                    languages={row.lit ? p.languagesLabel || "—" : "—"}
                    provenance={provenanceText(row.provenance, row.fanoutSource)}
                    isFirst={i === 0}
                  />
                ))}
                {!p.hasAnyDocs && (
                  <div style={{ fontSize: 12.5, color: "var(--muted)", lineHeight: 1.55, marginTop: 8 }}>
                    {translate("myMachinery:emptyStateNote")}
                  </div>
                )}
                <div
                  style={{
                    fontSize: 11, fontStyle: "italic", color: "var(--faint)",
                    marginTop: 8, lineHeight: 1.45,
                  }}
                >
                  {translate("myMachinery:languagesDisclaimer")}
                </div>
              </div>

              {/* Live machine data + CTA #2 */}
              <div
                style={{
                  ...section, paddingTop: 14, display: "flex", gap: 14,
                  alignItems: "flex-start",
                  borderTop: "1px solid var(--border-soft)",
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={heading}>
                    {translate("myMachinery:liveMachineData")}
                    <SectionBadge label={liveBadge.label} tone={liveBadge.tone} />
                  </div>
                  <div style={{ fontSize: 12, color: "var(--muted)", lineHeight: 1.55 }}>
                    {liveBody}
                  </div>
                  {connected && (
                    <div
                      style={{
                        display: "flex", alignItems: "center", gap: 6,
                        marginTop: 6, fontSize: 11, color: "var(--muted)",
                      }}
                    >
                      <span
                        style={{ width: 6, height: 6, borderRadius: 999, background: RD.live }}
                      />
                      {translate("myMachinery:connectedViaEdge")}
                    </div>
                  )}
                </div>
                {!connected && (
                  <OutlineCta
                    label={translate("myMachinery:requestIntegration")}
                    onClick={onRequestIntegration}
                  />
                )}
              </div>

              {/* Muted OEM package line — non-OEM models only */}
              {!p.oemLit && (
                <div style={{ ...section, paddingTop: 14, borderTop: "1px solid var(--border-soft)" }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "var(--faint)" }}>
                    {translate("myMachinery:officialOemPackage")}
                  </div>
                  <div style={{ fontSize: 12, color: "var(--faint)", marginTop: 2, lineHeight: 1.5 }}>
                    {translate("myMachinery:oemNotOffered", {
                      manufacturer:
                        manufacturerName || translate("myMachinery:theManufacturer"),
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default ReadinessPopupV2;
